import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Wrench, Database, AlertTriangle, CheckCircle, XCircle, 
  Loader2, RefreshCw, Link2, Download, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';


var authAxios = axios.create();
authAxios.interceptors.request.use(function(config) {
  var token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = 'Bearer ' + token;
  }
  return config;
});

export default function AdminMaintenanceTools() {
  // State for URL migration
  var [migrating, setMigrating] = useState(false);
  var [migrationResult, setMigrationResult] = useState(null);
  
  // State for restore slugs
  var [restoring, setRestoring] = useState(false);
  var [restoreResult, setRestoreResult] = useState(null);
  
  // State for fix modality
  var [fixingModality, setFixingModality] = useState(false);
  var [modalityResult, setModalityResult] = useState(null);
  
  // State for fix all slugs
  var [fixing, setFixing] = useState(false);
  var [fixResult, setFixResult] = useState(null);
  
  // State for image migration
  var [migratingImages, setMigratingImages] = useState(false);
  var [imageResult, setImageResult] = useState(null);
  var [migrationProgress, setMigrationProgress] = useState(null);
  var [forceRefresh, setForceRefresh] = useState(false);
  var [entityType, setEntityType] = useState('centers'); // 'centers' or 'workshops'
  
  // State for professional normalization
  var [normalizingProfessionals, setNormalizingProfessionals] = useState(false);
  var [professionalNormResult, setProfessionalNormResult] = useState(null);
  
  // State for email export
  var [exportingEmails, setExportingEmails] = useState(false);
  var [exportingCenters, setExportingCenters] = useState(false);
  var [emailExportType, setEmailExportType] = useState('todos');
  
  // State for SEO fix
  var [fixingSeo, setFixingSeo] = useState(false);
  var [seoResult, setSeoResult] = useState(null);
  
  // State for Google pictures migration
  var [migratingGooglePics, setMigratingGooglePics] = useState(false);
  var [googlePicsResult, setGooglePicsResult] = useState(null);
  
  // State for profile images sync
  var [syncingProfileImages, setSyncingProfileImages] = useState(false);
  var [syncProfileResult, setSyncProfileResult] = useState(null);
  
  // State for downloading old preview images
  var [downloadingOldImages, setDownloadingOldImages] = useState(false);
  var [downloadOldResult, setDownloadOldResult] = useState(null);
  
  // Download old preview images
  async function downloadOldImages() {
    if (!window.confirm('¿Descargar todas las imágenes de dominios de preview antiguos y corregir URLs en la base de datos?')) {
      return;
    }
    setDownloadingOldImages(true);
    setDownloadOldResult(null);
    try {
      var res = await authAxios.post(API + '/admin/download-old-images');
      setDownloadOldResult(res.data);
      toast.success('Migración de imágenes completada: ' + res.data.downloaded + ' descargadas, ' + res.data.urls_fixed_in_db + ' URLs corregidas');
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
      setDownloadOldResult({ error: e.message });
    }
    setDownloadingOldImages(false);
  }

  // Migrate Google pictures
  var [fixingDoctoralia, setFixingDoctoralia] = useState(false);
  var [doctoraliaResult, setDoctoraliaResult] = useState(null);

  async function fixDoctoraliaPhotos() {
    if (!window.confirm('¿Limpiar fotos rotas de profesionales de Doctoralia? Los que no tengan foto mostrarán un placeholder profesional.')) {
      return;
    }
    setFixingDoctoralia(true);
    setDoctoraliaResult(null);
    try {
      var res = await authAxios.post(API + '/admin/rescrape-doctoralia-photos', {}, { withCredentials: true });
      setDoctoraliaResult(res.data);
      toast.success('Fotos de Doctoralia procesadas: ' + res.data.migrated + ' arregladas');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
    setFixingDoctoralia(false);
  }

  async function migrateGooglePictures() {
    if (!window.confirm('¿Descargar y guardar localmente todas las fotos de perfil de Google? Esto evitará que las fotos expiren.')) {
      return;
    }
    
    setMigratingGooglePics(true);
    setGooglePicsResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/migrate-google-pictures', {}, { withCredentials: true });
      setGooglePicsResult(res.data);
      toast.success('Migración de fotos de Google completada: ' + res.data.migrated + ' migradas');
    } catch (err) {
      toast.error('Error en migración: ' + (err.response?.data?.detail || err.message));
    }
    
    setMigratingGooglePics(false);
  }
  
  // Sync profile images
  async function syncProfileImages() {
    setSyncingProfileImages(true);
    setSyncProfileResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/sync-profile-images', {}, { withCredentials: true });
      setSyncProfileResult(res.data);
      toast.success('Sincronización completada: ' + res.data.synced + ' actualizados');
    } catch (err) {
      toast.error('Error en sincronización: ' + (err.response?.data?.detail || err.message));
    }
    
    setSyncingProfileImages(false);
  }

  // Fix articles SEO
  async function runSeoFix() {
    if (!window.confirm('¿Ejecutar corrección de SEO para todos los artículos del blog? Esto actualizará: autor, meta description, keywords, imágenes y tiempo de lectura.')) {
      return;
    }
    
    setFixingSeo(true);
    setSeoResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/fix-articles-seo', {}, { withCredentials: true });
      setSeoResult({
        success: true,
        data: res.data
      });
      toast.success('SEO corregido: ' + res.data.updated + ' artículos actualizados');
    } catch (e) {
      console.error('SEO fix error:', e);
      setSeoResult({
        success: false,
        error: e.response?.data?.detail || e.message || 'Error desconocido'
      });
      toast.error('Error al corregir SEO');
    }
    
    setFixingSeo(false);
  }
  
  // Export centers directory (for Scribd) as Excel
  async function downloadCentersDirectory() {
    setExportingCenters(true);
    try {
      var token = localStorage.getItem('auth_token');
      var response = await fetch(API + '/admin/centers/export-excel', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!response.ok) throw new Error('Error al descargar');
      var blob = await response.blob();
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'centros_sinadicciones_' + new Date().toISOString().split('T')[0] + '.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Catálogo de centros descargado correctamente');
    } catch (err) {
      toast.error('Error al descargar: ' + err.message);
    } finally {
      setExportingCenters(false);
    }
  }

  // Export emails as Excel
  async function downloadEmails(tipo) {
    setExportingEmails(true);
    try {
      var token = localStorage.getItem('auth_token');
      var response = await fetch(API + '/admin/export-emails?tipo=' + tipo, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (!response.ok) throw new Error('Error al descargar');
      
      var blob = await response.blob();
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'emails_' + tipo + '_' + new Date().toISOString().split('T')[0] + '.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Excel descargado correctamente');
    } catch (err) {
      toast.error('Error al descargar: ' + err.message);
    } finally {
      setExportingEmails(false);
    }
  }
  async function runProfessionalNormalization() {
    if (!window.confirm('¿Normalizar datos de profesionales? Esto:\n\n• Convertirá los tipos de profesionales al formato estándar (ej: "therapist" → "Terapeuta")\n• Inferirá la región a partir de la ciudad para quienes no la tengan\n\nEsta operación es segura y puede ejecutarse múltiples veces.')) {
      return;
    }
    
    setNormalizingProfessionals(true);
    setProfessionalNormResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/normalize-professionals');
      setProfessionalNormResult({
        success: true,
        data: res.data.results
      });
      toast.success('Normalización de profesionales completada');
    } catch (err) {
      setProfessionalNormResult({
        success: false,
        error: err.response?.data?.detail || err.message
      });
      toast.error('Error al normalizar profesionales');
    } finally {
      setNormalizingProfessionals(false);
    }
  }

  // Migrate images to MongoDB in batches
  async function runImageMigration() {
    var entityLabel = entityType === 'workshops' ? 'talleres' : 'centros';
    var confirmMsg = forceRefresh 
      ? '¿RE-DESCARGAR todas las imágenes de ' + entityLabel + '? Esto reemplazará las fotos existentes con nuevas de Google Places. El proceso puede tardar varios minutos.'
      : '¿Migrar imágenes de ' + entityLabel + ' a MongoDB? Esto descargará las fotos de Google Places solo para los que no tengan imágenes. El proceso puede tardar varios minutos.';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setMigratingImages(true);
    setImageResult(null);
    setMigrationProgress({ current: 0, total: '?', batches: 0, entityType: entityType });
    
    try {
      var startFrom = 0;
      // Use smaller batch size for workshops since Google Places search takes longer
      var batchSize = entityType === 'workshops' ? 5 : 10;
      var hasMore = true;
      var totalProcessed = 0;
      var totalImages = 0;
      var totalStock = 0;
      var allErrors = [];
      var batchCount = 0;
      
      var totalReal = 0;
      
      while (hasMore) {
        batchCount++;
        setMigrationProgress({ current: startFrom, total: 'procesando...', batches: batchCount, entityType: entityType });
        
        var res = await authAxios.post(
          API + '/admin/migrate-images-to-mongodb?batch_size=' + batchSize + '&start_from=' + startFrom + '&entity_type=' + entityType + (forceRefresh ? '&force_refresh=true' : ''),
          {},
          { withCredentials: true, timeout: 180000 }
        );
        
        var data = res.data.results;
        totalProcessed += data.centers_processed || 0;
        totalImages += data.images_downloaded || 0;
        totalReal += data.centers_with_real_photos || 0;
        totalStock += data.centers_with_stock || 0;
        allErrors = allErrors.concat(data.errors || []);
        
        hasMore = data.has_more;
        startFrom = data.next_start || 0;
        
        setMigrationProgress({ 
          current: startFrom, 
          total: data.total_centers || '?', 
          batches: batchCount,
          images: totalImages
        });
        
        // Small delay between batches
        if (hasMore) {
          await new Promise(r => setTimeout(r, 500));
        }
      }
      
      setImageResult({
        success: true,
        data: {
          centers_processed: totalProcessed,
          images_downloaded: totalImages,
          centers_with_real_photos: totalReal,
          centers_with_stock: totalStock,
          total_images_in_db: res.data.results.total_images_in_db,
          errors: allErrors.slice(0, 10),
          batches_executed: batchCount
        }
      });
      toast.success('Migración completada: ' + totalReal + ' centros con fotos reales, ' + totalImages + ' imágenes descargadas');
      
    } catch (e) {
      console.error('Image migration error:', e);
      setImageResult({
        success: false,
        error: e.response?.data?.detail || e.message || 'Error desconocido'
      });
      toast.error('Error en la migración');
    }
    
    setMigratingImages(false);
    setMigrationProgress(null);
  }

  // Fix modality format
  async function runFixModality() {
    if (!window.confirm('¿Corregir campos de modalidad? Esto convertirá todos los campos "modality" que están guardados como texto a formato de lista.')) {
      return;
    }
    
    setFixingModality(true);
    setModalityResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/fix-modality-fields', {}, { withCredentials: true });
      setModalityResult({
        success: true,
        data: res.data
      });
      toast.success('Modalidad corregida: ' + res.data.fixed + ' centros actualizados');
    } catch (e) {
      console.error('Fix modality error:', e);
      setModalityResult({
        success: false,
        error: e.response?.data?.detail || e.message || 'Error desconocido'
      });
      toast.error('Error al corregir modalidad');
    }
    
    setFixingModality(false);
  }

  // Migrate to short SEO slugs
  async function runSlugMigration() {
    if (!window.confirm('¿Estás seguro de ejecutar la migración de URLs? Esto actualizará los slugs de todos los centros a un formato SEO-friendly corto.')) {
      return;
    }
    
    setMigrating(true);
    setMigrationResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/migrate-center-slugs', {}, { withCredentials: true });
      setMigrationResult({
        success: true,
        data: res.data
      });
      toast.success('Migración completada: ' + res.data.updated + ' centros actualizados');
    } catch (e) {
      console.error('Migration error:', e);
      setMigrationResult({
        success: false,
        error: e.response?.data?.detail || e.message || 'Error desconocido'
      });
      toast.error('Error en la migración');
    }
    
    setMigrating(false);
  }

  // Restore original long slugs
  async function runRestoreOriginalSlugs() {
    if (!window.confirm('¿Estás seguro? Esto restaurará las URLs largas originales basadas en el nombre completo del centro.')) {
      return;
    }
    
    setRestoring(true);
    setRestoreResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/fix-all-slugs-from-name', {}, { withCredentials: true });
      setRestoreResult({
        success: true,
        data: res.data
      });
      toast.success('Slugs restaurados: ' + res.data.updated + ' centros');
    } catch (e) {
      console.error('Restore error:', e);
      setRestoreResult({
        success: false,
        error: e.response?.data?.detail || e.message || 'Error desconocido'
      });
      toast.error('Error al restaurar slugs');
    }
    
    setRestoring(false);
  }

  // Fix all slugs
  async function runFixAllSlugs() {
    if (!window.confirm('¿Corregir TODOS los slugs? Esto genera slugs a partir del nombre actual de cada centro.')) {
      return;
    }
    
    setFixing(true);
    setFixResult(null);
    
    try {
      var res = await authAxios.post(API + '/admin/fix-all-slugs', {}, { withCredentials: true });
      setFixResult({
        success: true,
        data: res.data
      });
      toast.success('Slugs corregidos: ' + res.data.updated + ' centros actualizados');
    } catch (e) {
      console.error('Fix error:', e);
      setFixResult({
        success: false,
        error: e.response?.data?.detail || e.message || 'Error desconocido'
      });
      toast.error('Error al corregir slugs');
    }
    
    setFixing(false);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
          <Wrench className="w-6 h-6 text-orange-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-50">Herramientas de Mantenimiento</h2>
          <p className="text-sm text-slate-400">Utilidades para administrar la plataforma</p>
        </div>
      </div>

      {/* Fix Modality Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-400" />
            Migrar Imágenes a MongoDB
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  <strong>BUSCA FOTOS REALES:</strong> Para cada centro, busca en Google Places y descarga las fotos actuales del lugar.
                </p>
                <ul className="text-slate-400 text-xs mt-2 space-y-1 list-disc list-inside">
                  <li>Busca cada centro por nombre en Google Places</li>
                  <li>Descarga TODAS las fotos disponibles (hasta 10 por centro)</li>
                  <li>Guarda las fotos permanentemente en MongoDB</li>
                  <li>Si no encuentra fotos, asigna imagen de stock</li>
                  <li>Proceso en lotes para evitar timeout (~10-15 min total)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Force refresh checkbox */}
          <label className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600">
            <input 
              type="checkbox"
              checked={forceRefresh}
              onChange={function(e) { setForceRefresh(e.target.checked); }}
              className="w-5 h-5 rounded border-slate-600 text-amber-500 focus:ring-amber-500"
              disabled={migratingImages}
            />
            <div>
              <p className="text-white font-medium">Forzar re-descarga</p>
              <p className="text-xs text-slate-400">
                Reemplaza fotos existentes. Útil si las imágenes actuales están repetidas o son de baja calidad.
              </p>
            </div>
          </label>

          {/* Entity type selector */}
          <div className="flex gap-2">
            <button
              onClick={function() { setEntityType('centers'); }}
              disabled={migratingImages}
              className={'flex-1 px-4 py-3 rounded-lg border-2 transition-all ' +
                (entityType === 'centers' 
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                  : 'border-slate-700 text-slate-400 hover:border-slate-600')}
            >
              <p className="font-medium">Centros</p>
              <p className="text-xs opacity-75">Buscar fotos de centros de rehabilitación</p>
            </button>
            <button
              onClick={function() { setEntityType('workshops'); }}
              disabled={migratingImages}
              className={'flex-1 px-4 py-3 rounded-lg border-2 transition-all ' +
                (entityType === 'workshops' 
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                  : 'border-slate-700 text-slate-400 hover:border-slate-600')}
            >
              <p className="font-medium">Talleres</p>
              <p className="text-xs opacity-75">Buscar fotos de talleres y actividades</p>
            </button>
          </div>

          <Button 
            onClick={runImageMigration} 
            disabled={migratingImages}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="migrate-images-btn"
          >
            {migratingImages ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Migrando {entityType === 'workshops' ? 'talleres' : 'centros'} en lotes...
              </>
            ) : (
              <>
                <Database className="w-4 h-4 mr-2" />
                Migrar Imágenes de {entityType === 'workshops' ? 'Talleres' : 'Centros'}
              </>
            )}
          </Button>

          {migrationProgress && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-emerald-400 text-sm font-medium">
                Procesando lote {migrationProgress.batches}...
              </p>
              <p className="text-slate-400 text-xs mt-1">
                {migrationProgress.entityType === 'workshops' ? 'Talleres' : 'Centros'}: {migrationProgress.current} / {migrationProgress.total}
                {migrationProgress.images > 0 && ` | Imágenes: ${migrationProgress.images}`}
              </p>
            </div>
          )}

          {imageResult && (
            <ResultBox result={imageResult} successText="Migración completada exitosamente">
              {imageResult.success && imageResult.data && (
                <div className="text-sm text-slate-300 space-y-1">
                  <p className="font-semibold text-emerald-400 mb-2">Resultados:</p>
                  <p className="ml-2">Centros procesados: {imageResult.data.centers_processed}</p>
                  <p className="ml-2 text-emerald-400">✓ Con fotos reales: {imageResult.data.centers_with_real_photos}</p>
                  <p className="ml-2 text-amber-400">○ Con stock: {imageResult.data.centers_with_stock}</p>
                  <p className="ml-2">Imágenes descargadas: {imageResult.data.images_downloaded}</p>
                  <p className="ml-2">Total imágenes en BD: {imageResult.data.total_images_in_db}</p>
                  <p className="ml-2">Lotes ejecutados: {imageResult.data.batches_executed}</p>
                  {imageResult.data.errors && imageResult.data.errors.length > 0 && (
                    <p className="text-red-400 mt-2">Errores: {imageResult.data.errors.length}</p>
                  )}
                </div>
              )}
            </ResultBox>
          )}
        </CardContent>
      </Card>

      {/* Fix Modality Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Corregir Formato de Modalidad
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Corrige el campo <strong>modality</strong> de centros que se guardó como texto en vez de lista.
                </p>
                <ul className="text-slate-400 text-xs mt-2 space-y-1 list-disc list-inside">
                  <li>Convierte "residencial, ambulatorio" a ["residencial", "ambulatorio"]</li>
                  <li>Corrige problemas de visualización en filtros y badges</li>
                  <li>Operación segura, puede ejecutarse múltiples veces</li>
                </ul>
              </div>
            </div>
          </div>

          <Button 
            onClick={runFixModality} 
            disabled={fixingModality}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="fix-modality-btn"
          >
            {fixingModality ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Corrigiendo...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Corregir Formato de Modalidad
              </>
            )}
          </Button>

          {modalityResult && (
            <ResultBox result={modalityResult} successText="Modalidad corregida exitosamente">
              {modalityResult.success && modalityResult.data && (
                <div className="text-sm text-slate-300 space-y-1">
                  <p><strong>Total de centros:</strong> {modalityResult.data.total}</p>
                  <p><strong>Centros corregidos:</strong> {modalityResult.data.fixed}</p>
                </div>
              )}
            </ResultBox>
          )}
        </CardContent>
      </Card>

      {/* Professional Normalization Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-400" />
            Normalizar Datos de Profesionales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Normaliza los datos de profesionales para que funcionen correctamente los filtros de búsqueda.
                </p>
                <ul className="text-slate-400 text-xs mt-2 space-y-1 list-disc list-inside">
                  <li>Convierte tipos al formato estándar: "therapist" → "Terapeuta"</li>
                  <li>Infiere la región a partir de la ciudad (ej: "Santiago" → "Metropolitana")</li>
                  <li>Operación segura, puede ejecutarse múltiples veces</li>
                </ul>
              </div>
            </div>
          </div>

          <Button 
            onClick={runProfessionalNormalization} 
            disabled={normalizingProfessionals}
            className="bg-purple-500 hover:bg-purple-600 text-white"
            data-testid="normalize-professionals-btn"
          >
            {normalizingProfessionals ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Normalizando...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Normalizar Profesionales
              </>
            )}
          </Button>

          {professionalNormResult && (
            <ResultBox result={professionalNormResult} successText="Normalización completada">
              {professionalNormResult.success && professionalNormResult.data && (
                <div className="text-sm text-slate-300 space-y-1">
                  <p><strong>Total procesados:</strong> {professionalNormResult.data.total}</p>
                  <p className="text-purple-400"><strong>Tipos normalizados:</strong> {professionalNormResult.data.type_normalized}</p>
                  <p className="text-emerald-400"><strong>Regiones agregadas:</strong> {professionalNormResult.data.region_added}</p>
                  <p className="text-slate-400"><strong>Ya correctos:</strong> {professionalNormResult.data.already_correct}</p>
                  {professionalNormResult.data.examples && professionalNormResult.data.examples.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-400 mb-1">Ejemplos de cambios:</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {professionalNormResult.data.examples.map(function(ex, idx) {
                          return (
                            <div key={idx} className="text-xs font-mono bg-slate-800 p-2 rounded">
                              <span className="text-slate-300">{ex.name}</span>
                              {ex.changes.map(function(change, cIdx) {
                                return <div key={cIdx} className="text-purple-400 ml-2">→ {change}</div>;
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ResultBox>
          )}
        </CardContent>
      </Card>

      {/* Centers Directory Export Card (for Scribd / catalog distribution) */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Download className="w-5 h-5 text-emerald-400" />
            Catálogo de Centros (Excel para Scribd)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Genera un Excel con todos los centros públicos para subirlo a Scribd o distribuir como catálogo.
                </p>
                <ul className="text-slate-400 text-xs mt-2 space-y-1 list-disc list-inside">
                  <li>Columnas: Nombre, Descripción, Dirección, Modalidad, Región, Teléfono, Email, Plan</li>
                  <li>Enlace clickeable al perfil público (https://sinadicciones.org/centro/&#123;slug&#125;)</li>
                  <li>Ordenado por plan (Elite primero) y luego alfabético</li>
                </ul>
              </div>
            </div>
          </div>
          <Button
            onClick={downloadCentersDirectory}
            disabled={exportingCenters}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="export-centers-directory-btn"
          >
            {exportingCenters ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando Excel…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Descargar Catálogo de Centros (.xlsx)</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Email Export Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan-400" />
            Exportar Emails para Campañas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Descarga un archivo Excel con los emails de la plataforma para usar en campañas de marketing (Omnisend, Mailchimp, etc.)
                </p>
                <ul className="text-slate-400 text-xs mt-2 space-y-1 list-disc list-inside">
                  <li>Incluye: Nombre, Email, Ciudad, Región, Plan, Verificación</li>
                  <li>Formato: Excel (.xlsx) compatible con todas las plataformas</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Button 
              onClick={function() { downloadEmails('todos'); }}
              disabled={exportingEmails}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="export-all-emails-btn"
            >
              {exportingEmails ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Download className="w-4 h-4 mr-1" />
                  Todos
                </>
              )}
            </Button>
            <Button 
              onClick={function() { downloadEmails('centros'); }}
              disabled={exportingEmails}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Download className="w-4 h-4 mr-1" />
              Centros
            </Button>
            <Button 
              onClick={function() { downloadEmails('profesionales'); }}
              disabled={exportingEmails}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Download className="w-4 h-4 mr-1" />
              Profesionales
            </Button>
            <Button 
              onClick={function() { downloadEmails('talleres'); }}
              disabled={exportingEmails}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Download className="w-4 h-4 mr-1" />
              Talleres
            </Button>
            <Button 
              onClick={function() { downloadEmails('usuarios'); }}
              disabled={exportingEmails}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Download className="w-4 h-4 mr-1" />
              Usuarios
            </Button>
          </div>

          <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
            <p className="text-cyan-400 text-sm">
              <strong>Excel SENDA:</strong> Descarga directa en{' '}
              <a 
                href="/downloads/emails_senda.xlsx" 
                download
                className="underline hover:text-cyan-300"
              >
                /downloads/emails_senda.xlsx
              </a>
              {' '}(43 contactos de oficinas SENDA)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Blog SEO Fix Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-400" />
            Corregir SEO de Artículos del Blog
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Corrige automáticamente todos los campos SEO de los artículos del blog:
                </p>
                <ul className="text-slate-400 text-xs mt-2 space-y-1 list-disc list-inside">
                  <li>Autor → sinadicciones.org</li>
                  <li>Meta description (generada si falta)</li>
                  <li>Meta keywords (generadas desde tags y título)</li>
                  <li>Imagen destacada (asignada por categoría)</li>
                  <li>Tiempo de lectura (calculado automáticamente)</li>
                  <li>Elimina artículos de prueba</li>
                </ul>
              </div>
            </div>
          </div>

          <Button 
            onClick={runSeoFix}
            disabled={fixingSeo}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white"
            data-testid="fix-blog-seo-btn"
          >
            {fixingSeo ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Corrigiendo SEO...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Corregir SEO de Artículos
              </>
            )}
          </Button>

          {seoResult && (
            <div className={`p-4 rounded-lg border ${seoResult.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              {seoResult.success ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">SEO Corregido Exitosamente</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-slate-800/50 rounded">
                      <div className="text-2xl font-bold text-purple-400">{seoResult.data.total_articles}</div>
                      <div className="text-xs text-slate-400">Total</div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded">
                      <div className="text-2xl font-bold text-green-400">{seoResult.data.updated}</div>
                      <div className="text-xs text-slate-400">Actualizados</div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded">
                      <div className="text-2xl font-bold text-red-400">{seoResult.data.deleted}</div>
                      <div className="text-xs text-slate-400">Eliminados</div>
                    </div>
                  </div>
                  {seoResult.data.details && seoResult.data.details.length > 0 && (
                    <div className="mt-4 max-h-48 overflow-y-auto">
                      <p className="text-slate-400 text-xs mb-2">Detalles:</p>
                      {seoResult.data.details.slice(0, 10).map(function(item, idx) {
                        return (
                          <div key={idx} className="text-xs text-slate-500 py-1 border-b border-slate-700/50">
                            {item.action === 'deleted' ? '🗑️' : '✅'} {item.title}...
                            {item.fields && <span className="text-purple-400 ml-2">({item.fields.length} campos)</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <span className="text-red-400">{seoResult.error}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Download Old Preview Images Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Download className="w-5 h-5 text-orange-400" />
            Recuperar Imágenes de Preview Antiguo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-orange-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Descarga imágenes almacenadas en dominios de preview anteriores y corrige las URLs en la base de datos.
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Esto soluciona las fotos rotas de centros, profesionales y talleres que se subieron en entornos anteriores.
                </p>
              </div>
            </div>
          </div>

          <Button 
            onClick={downloadOldImages}
            disabled={downloadingOldImages}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="download-old-images-btn"
          >
            {downloadingOldImages ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Descargando imágenes...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Recuperar Imágenes y Corregir URLs
              </>
            )}
          </Button>

          {downloadOldResult && !downloadOldResult.error && (
            <div className="p-4 rounded-lg border bg-green-500/10 border-green-500/30">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Migración Completada</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div className="p-3 bg-slate-800/50 rounded">
                  <div className="text-2xl font-bold text-orange-400">{downloadOldResult.total_urls_found}</div>
                  <div className="text-xs text-slate-400">URLs encontradas</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded">
                  <div className="text-2xl font-bold text-green-400">{downloadOldResult.downloaded}</div>
                  <div className="text-xs text-slate-400">Descargadas</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded">
                  <div className="text-2xl font-bold text-blue-400">{downloadOldResult.urls_fixed_in_db}</div>
                  <div className="text-xs text-slate-400">URLs corregidas</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded">
                  <div className="text-2xl font-bold text-red-400">{downloadOldResult.failed}</div>
                  <div className="text-xs text-slate-400">Fallidas</div>
                </div>
              </div>
            </div>
          )}
          {downloadOldResult && downloadOldResult.error && (
            <div className="p-4 rounded-lg border bg-red-500/10 border-red-500/30">
              <p className="text-red-400 text-sm">Error: {downloadOldResult.error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Pictures Migration Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Download className="w-5 h-5 text-cyan-400" />
            Migración de Fotos de Google
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Descarga y guarda localmente las fotos de perfil de usuarios que se registraron con Google.
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Esto evita que las fotos expiren después de un tiempo.
                </p>
              </div>
            </div>
          </div>

          <Button 
            onClick={migrateGooglePictures}
            disabled={migratingGooglePics}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="migrate-google-pics-btn"
          >
            {migratingGooglePics ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Migrando fotos...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Migrar Fotos de Google
              </>
            )}
          </Button>

          {googlePicsResult && (
            <div className="p-4 rounded-lg border bg-green-500/10 border-green-500/30">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Migración Completada</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-slate-800/50 rounded">
                  <div className="text-2xl font-bold text-cyan-400">{googlePicsResult.total_processed}</div>
                  <div className="text-xs text-slate-400">Procesados</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded">
                  <div className="text-2xl font-bold text-green-400">{googlePicsResult.migrated}</div>
                  <div className="text-xs text-slate-400">Migrados</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded">
                  <div className="text-2xl font-bold text-red-400">{googlePicsResult.failed}</div>
                  <div className="text-xs text-slate-400">Fallidos</div>
                </div>
              </div>
            </div>
          )}
          
          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-slate-400 text-sm mb-3">
              <strong>Arreglar fotos de Doctoralia:</strong> Limpia fotos rotas de profesionales importados. Los que no tengan foto válida mostrarán un avatar placeholder.
            </p>
            <Button 
              onClick={fixDoctoraliaPhotos}
              disabled={fixingDoctoralia}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="fix-doctoralia-btn"
            >
              {fixingDoctoralia ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando fotos...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Arreglar Fotos de Doctoralia</>
              )}
            </Button>
            {doctoraliaResult && (
              <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-sm text-green-400">
                Procesados: {doctoraliaResult.total} | Arreglados: {doctoraliaResult.migrated} | En MongoDB: {doctoraliaResult.already_in_mongo} | Fallidos: {doctoraliaResult.failed}
              </div>
            )}
          </div>

          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-slate-400 text-sm mb-3">
              <strong>Sincronizar fotos de perfil:</strong> Actualiza las fotos de usuarios que subieron su propia imagen.
            </p>
            <Button 
              onClick={syncProfileImages}
              disabled={syncingProfileImages}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              variant="outline"
            >
              {syncingProfileImages ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sincronizar Fotos de Perfil
                </>
              )}
            </Button>
            
            {syncProfileResult && (
              <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                <span className="text-green-400">{syncProfileResult.synced} de {syncProfileResult.total_professionals} profesionales sincronizados</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* URL Migration Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-400" />
            Migración de URLs de Centros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Genera slugs SEO-friendly cortos para las URLs de centros.
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Ejemplo: "Centro Vida Nueva Santiago" → "vida-nueva-santiago"
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={runSlugMigration} 
              disabled={migrating}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="migrate-slugs-btn"
            >
              {migrating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrando...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Migrar a URLs Cortas (SEO)
                </>
              )}
            </Button>
          </div>

          {migrationResult && (
            <ResultBox result={migrationResult} successText="Migración completada">
              {migrationResult.success && migrationResult.data && (
                <div className="text-sm text-slate-300 space-y-1">
                  <p><strong>Centros actualizados:</strong> {migrationResult.data.updated}</p>
                  {migrationResult.data.examples && migrationResult.data.examples.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-400 mb-1">Ejemplos:</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {migrationResult.data.examples.slice(0, 5).map(function(ex, idx) {
                          return (
                            <div key={idx} className="text-xs font-mono bg-slate-800 p-2 rounded">
                              <span className="text-slate-400">{ex.name}</span><br />
                              <span className="text-emerald-400">→ /{ex.new_slug}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ResultBox>
          )}
        </CardContent>
      </Card>

      {/* Restore Slugs Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-400" />
            Restaurar Slugs Originales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-sm">
                  Restaura las URLs largas basadas en el nombre completo del centro.
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Útil si las URLs cortas causan conflictos.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={runRestoreOriginalSlugs} 
              disabled={restoring}
              variant="outline"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              data-testid="restore-slugs-btn"
            >
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restaurando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Restaurar Slugs Largos
                </>
              )}
            </Button>

            <Button 
              onClick={runFixAllSlugs} 
              disabled={fixing}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="fix-all-slugs-btn"
            >
              {fixing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Corrigiendo...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Corregir TODOS los Slugs
                </>
              )}
            </Button>
          </div>

          {restoreResult && (
            <ResultBox result={restoreResult} successText="Slugs restaurados">
              {restoreResult.success && restoreResult.data && (
                <p className="text-sm text-slate-300">
                  <strong>Centros actualizados:</strong> {restoreResult.data.updated}
                </p>
              )}
            </ResultBox>
          )}

          {fixResult && (
            <ResultBox result={fixResult} successText="Slugs corregidos">
              {fixResult.success && fixResult.data && (
                <p className="text-sm text-slate-300">
                  <strong>Centros actualizados:</strong> {fixResult.data.updated}
                </p>
              )}
            </ResultBox>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Helper component for result boxes
function ResultBox({ result, successText, children }) {
  return (
    <div className={'p-4 rounded-lg border ' + (result.success ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30')}>
      {result.success ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">{successText}</span>
          </div>
          {children}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-red-400">
          <XCircle className="w-5 h-5" />
          <span>Error: {result.error}</span>
        </div>
      )}
    </div>
  );
}
